import { create } from 'zustand';
import { supabase } from '../lib/supabase';
import { planStatus } from '../utils/planLimits';
import { recordLoginSession } from '../lib/apiClient';
import { authFetch, getAccessToken } from '../lib/authFetch';
import { getSignupSource, trackEvent } from '../lib/analytics';

// ─── Selectors ──────────────────────────────────────────────────────
// Use these with `useAuthStore(selectIsPro)` so they stay REACTIVE to
// profile/session changes.
//
// We deliberately DON'T expose these as getters on the store state.
// Zustand's internal shallow merge (`{ ...state, ...partial }`) evaluates
// getters at set() time and captures the CURRENT value into the next
// state as a plain property — so `set({ profile: proProfile })` would
// run the getter with the OLD profile, get `false`, and stamp that stale
// `false` onto the new state. Subsequent reads return the stale value.
// (This is the bug that hid Pro users behind an "Upgrade required"
// banner in Projektverwaltung.)
export const selectIsAdmin = (state) => state.profile?.role === 'admin';
export const selectIsPro = (state) => ['pro', 'team'].includes(state.profile?.plan);
export const selectIsFree = (state) => state.profile?.plan === 'free';
export const selectIsLoggedIn = (state) => !!state.session;

// `selectPlanStatus` returns the fully-computed quota snapshot for
// the current profile. Components that need `total` / `used` /
// `remaining` should use this instead of the legacy method-on-state
// helpers (`canCalculate()` / `remainingCalculations()`), which look
// reactive but don't always re-render when profile changes underneath
// them — same zustand-getter staleness class of bug that hid Pro
// users behind a Free upsell earlier.
export const selectPlanStatus = (state) => planStatus(state.profile);

// Arbeit nach einem Auth-Ereignis, außerhalb des Supabase-Listeners
// (siehe Kommentar dort). Reihenfolge bleibt: erst die Sitzung beim
// Server registrieren, dann weitere API-Aufrufe.
async function syncAfterAuthEvent(session, get) {
  // Inzwischen abgemeldet oder neue Session: nichts mehr registrieren.
  if (get().session?.access_token !== session.access_token) return;
  await recordLoginSession(session);
  await get().fetchProfile();
  await get().fetchRemainingCalculations();
  try {
    await supabase
      .from('profiles')
      .update({ last_login: new Date().toISOString() })
      .eq('id', session.user.id);
  } catch (updateErr) {
    console.warn('Could not update last_login:', updateErr);
  }
}

export const useAuthStore = create((set, get) => ({
  user: null,
  profile: null,
  session: null,
  loading: true,
  initialized: false,
  // True, sobald Supabase ein PASSWORD_RECOVERY-Event meldet (Klick auf
  // den Reset-Link). ResetPasswordPage schaltet dann auf das „neues
  // Passwort setzen"-Formular um. Wird beim signOut wieder false.
  passwordRecovery: false,

  // NB: Plan-status (canCalculate / remaining / total) used to live as
  // methods on the store. They were removed because consumers reading
  // them via destructuring got a stale snapshot — the function value
  // was captured at render time, called later against the *current*
  // store, and the component never re-rendered when the profile
  // changed underneath. Use `useAuthStore(selectPlanStatus)` instead.

  fetchRemainingCalculations: async () => {
    const { session } = get();
    if (!session) return 0;
    
    try {
      const res = await fetch('/api/user/remaining-calculations', {
        headers: { Authorization: `Bearer ${session.access_token}` }
      });
      
      if (res.status === 401) {
        console.warn('Unauthorized fetch - session might be stale or keys are placeholders');
        return 0; // Return safe default
      }
      
      if (!res.ok) throw new Error('Fetch failed');
      const data = await res.json();
      // Update profile with fresh counts if needed
      if (data.used !== undefined) {
        set(state => ({
          profile: {
            ...state.profile,
            calculations_this_month: data.used,
            calculations_reset_at: data.reset_at
          }
        }));
      }
      return data.remaining ?? 0;
    } catch (err) {
      console.error('Error fetching remaining calculations:', err);
      return 0;
    }
  },

  // Initialize auth listener
  initialize: async () => {
    // Recovery-Flow früh am URL-Hash erkennen (der ist noch da, BEVOR
    // getSession ihn verarbeitet + entfernt). Wichtig: für die temporäre
    // Recovery-Session dürfen wir KEINE Device-Registrierung + KEINEN
    // Counter-Fetch auslösen — der käme mit 401 (session_not_registered)
    // zurück und triggert den Device-Limit-Auto-Logout (Task #25), der die
    // Recovery-Session sofort wieder killt. Folge wäre, dass die
    // ResetPasswordPage aufs E-Mail-Formular zurückfällt (Endlosschleife).
    const isRecovery = typeof window !== 'undefined' && window.location.hash.includes('type=recovery');
    if (isRecovery) set({ passwordRecovery: true });
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        set({ session, user: session.user });
        if (!isRecovery) {
          await get().fetchProfile();
          await get().fetchRemainingCalculations();
        }
      }
    } catch (err) {
      console.error('Failed to initialize auth session:', err);
    } finally {
      set({ loading: false, initialized: true });
    }

    // Listen for auth changes
    try {
      // Der Listener darf NIE auf Supabase warten (A3, 2026-10-20): Beim
      // Token-Refresh hält auth-js 2.103 seine Sperre und wartet auf alle
      // Listener. Eine Supabase-Abfrage hier (fetchProfile, last_login)
      // wartet ihrerseits auf die Sperre: der Client blockiert sich
      // dauerhaft, jedes spätere getSession() und damit jedes Speichern
      // hängt. Deshalb nur den Store synchron setzen und den Rest per
      // setTimeout nach dem Listener anstoßen.
      // Regressionstest: src/stores/authStore.listener.test.js
      supabase.auth.onAuthStateChange((event, session) => {
        // Reset-Link angeklickt: Supabase legt eine Recovery-Session an
        // und feuert PASSWORD_RECOVERY. Flag setzen, damit die
        // ResetPasswordPage das Passwort-setzen-Formular zeigt statt
        // erneut nach der E-Mail zu fragen (sonst Endlosschleife).
        if (event === 'PASSWORD_RECOVERY') set({ passwordRecovery: true });
        set({ session, user: session?.user || null });
        // Im Recovery-Flow KEINE Device-Registrierung / Counter-Fetch /
        // last_login — der 401 daraus würde den Auto-Logout auslösen und
        // die Recovery-Session killen (siehe Kommentar in initialize). Die
        // temporäre Session muss nur leben, bis updateUser({password}) durch ist.
        if (session && !get().passwordRecovery) {
          // Register this session in device_sessions BEFORE any other
          // API call goes out — the enforceDeviceLimit middleware
          // checks against the device_sessions table on every /api
          // request, and a missing entry would 401 right back.
          // Triggered for INITIAL_SESSION too (page reload) so a
          // server-side cleanup (90-day retention or replacement)
          // doesn't strand the user.
          setTimeout(() => { syncAfterAuthEvent(session, get); }, 0);
        } else if (!session) {
          set({ profile: null });
        }
      });
    } catch (err) {
      console.error('Failed to setup auth listener:', err);
    }
  },

  fetchProfile: async () => {
    const { user } = get();
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', user.id)
        .single();
      
      if (error) {
        console.error('Error fetching profile:', error);
      } else if (data) {
        set({ profile: data });
      }
    } catch (err) {
      console.error('Critical error in fetchProfile:', err);
    }
  },

  // Auth actions
  signUp: async (email, password, tos_version = '1.0') => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signUp({ 
      email, 
      password,
      options: {
        data: {
          tos_accepted_at: new Date().toISOString(),
          tos_version: tos_version,
          // Kanal-Attribution: utm_source der Einstiegs-URL (oder 'direct').
          // Der handle_new_user-Trigger schreibt das nach profiles.signup_source.
          signup_source: getSignupSource()
        }
      }
    });
    set({ loading: false });
    if (error) throw error;
    return data;
  },

  signIn: async (email, password) => {
    set({ loading: true });
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      set({ loading: false });
      throw error;
    }
    // Session direkt im Store setzen, statt auf den
    // onAuthStateChange-Listener zu warten. Sonst sieht das nach
    // signIn() folgende navigate('/') in der LoginPage noch
    // session=null im Store, und AuthGuard redirected zurück
    // zur /login-Seite (Symptom: „Anmelden-Klick tut nichts, erst
    // Reload hilft"). Das wurde mit Phase 25 (Device-Limit)
    // sichtbarer, weil der Listener jetzt erst nach
    // recordLoginSession + fetchProfile fertig ist. Der Listener
    // setzt dieselben Werte nochmal idempotent.
    set({
      session: data.session,
      user: data.user,
      loading: false
    });
    // Sitzung beim Server registrieren, bevor der Aufrufer weiter
    // navigiert und die ersten /api-Anfragen schickt (Device-Limit).
    // Früher geschah das implizit, weil signInWithPassword auf den
    // Listener wartete; der stößt die Registrierung jetzt nur noch an.
    await recordLoginSession(data.session);
    // Profil vor der Navigation laden, sonst rendert die App kurz ohne
    // Plan (Pro sieht Upgrade-Hinweise, Free gilt kurz als „nicht Free“).
    await get().fetchProfile();
    // Funnel-Messung (B4): nur Plan-Typ, keine Kennung.
    trackEvent('Login', { plan: get().profile?.plan || 'free' });
    return data;
  },

  signOut: async () => {
    // Lokalen State sofort clearen, BEVOR auf das Supabase-SDK
    // gewartet wird: der `await` hängt am Backend-Call, der den
    // Refresh-Token serverseitig revoket — kann ein paar 100 ms
    // dauern. In dieser Zeit war AuthGuard noch der Meinung, die
    // Session sei gültig, und der User sah weder Logout-Effekt
    // noch Redirect (Symptom: "Klick reagiert nicht, erst Reload
    // hilft"). Durch das frühe Reset reagiert die UI sofort,
    // unabhängig vom SDK-Latenz.
    set({ user: null, profile: null, session: null, passwordRecovery: false });
    try {
      // scope: 'local' ist hier entscheidend. Supabase-Default ist
      // 'global', was alle Refresh-Tokens des Users server-seitig
      // revoket — also auch die Tokens auf anderen Geräten desselben
      // Accounts. Solange wir die einzige Session waren, war das
      // egal. Mit dem Device-Limit aus Task #25 sind aber typisch
      // 2 Sessions parallel: wenn Gerät A nach Eviction signOut
      // macht, würde 'global' Gerät B's frischen Token auch killen,
      // und B sieht beim nächsten API-Call „Ungültiger Token /
      // Auth session missing!". 'local' meldet nur die eine
      // Session ab, B bleibt unbehelligt.
      await supabase.auth.signOut({ scope: 'local' });
    } catch (err) {
      // Lokale Session ist bereits geleert — ein Netzwerk-Fehler
      // beim Token-Revoke ist hier nicht fatal (Token läuft so
      // oder so in <1 h ab) und sollte das User-Feedback nicht
      // blockieren.
      console.warn('[auth] supabase.signOut threw, local state already cleared:', err.message);
    }
  },

  resetPassword: async (email) => {
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`
    });
    if (error) throw error;
  },

  updateEmail: async (newEmail) => {
    const { error } = await supabase.auth.updateUser({ email: newEmail });
    if (error) throw error;
  },

  updatePassword: async (newPassword) => {
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    if (error) throw error;
  },

  updateProfile: async (updates) => {
    const { user } = get();
    if (!user) throw new Error('Not logged in');
    const { data, error } = await supabase
      .from('profiles')
      .update(updates)
      .eq('id', user.id)
      .select()
      .single();
    if (error) throw error;
    set({ profile: data });
    return data;
  },

  incrementCalculation: async () => {
    // Pull a fresh session from the Supabase SDK rather than trusting
    // the in-store copy — the store's session can briefly lag the SDK
    // (e.g. user signed out as Pro and back in as Free seconds later),
    // and the stale access_token surfaces as a server-side "Ungültiger
    // Token" 401 instead of incrementing.
    // getAccessToken also refreshes a token that expired while the tab
    // sat in the background; authFetch retries a 401 once.
    const token = await getAccessToken();
    if (!token) throw new Error('Nicht angemeldet - bitte zuerst einloggen.');

    try {
      const res = await authFetch('/api/user/calculations', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Counter update failed');
      }
      const data = await res.json();
      // Update local profile state
      set(state => ({
        profile: {
          ...state.profile,
          calculations_this_month: data.used
        }
      }));
      return data;
    } catch (err) {
      console.error('Error incrementing calculation:', err);
      throw err;
    }
  }
}));
